import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { FOCUS_RING_OFFSET } from "../../lib/focus-ring";
import { cn } from "../../lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90",
        destructive: "bg-destructive text-white shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        ai: "bg-ai text-ai-foreground shadow-sm hover:bg-ai/90",
        // Opt-in variants (NO-791). Each renders exactly like the classic
        // variant it names until the always theme is on: every utility that
        // differs reads a value only [data-theme="always"] defines, with the
        // classic value as its fallback (README, "Opt-in variants").
        // The dark primary with a press edge: classic default; under the
        // theme an olive pill in display type that sinks when pressed.
        press:
          "bg-primary text-primary-foreground hover:bg-primary/90 shadow-(--shadow-press-edge,0_1px_2px_rgba(40,3,15,0.05)) font-(family-name:--button-press-font) font-(weight:--button-press-weight,500) active:translate-y-(--button-press-travel,0px) active:shadow-(--shadow-press-active,0_1px_2px_rgba(40,3,15,0.05))",
        // The quiet inline link: classic link; under the theme always
        // underlined, 1px at a 3px offset, semibold.
        "quiet-link":
          "text-primary underline-offset-(--link-quiet-offset,4px) decoration-(length:--link-quiet-thickness,auto) [text-decoration-line:var(--link-quiet-line,none)] font-(weight:--link-quiet-weight,500) hover:underline",
        // The utility button: classic outline; under the theme card-white with
        // a 1px line and no shadow, and an olive inset while pressed or open.
        utility:
          "border border-(color:--button-utility-border,var(--input)) bg-(--button-utility-bg,var(--background)) shadow-(--button-utility-shadow,0_1px_2px_rgba(40,3,15,0.05)) font-(weight:--button-utility-weight,500) hover:bg-accent hover:text-accent-foreground aria-pressed:border-(color:--button-selected-border,var(--input)) aria-pressed:shadow-(--button-selected,0_1px_2px_rgba(40,3,15,0.05)) aria-expanded:border-(color:--button-selected-border,var(--input)) aria-expanded:shadow-(--button-selected,0_1px_2px_rgba(40,3,15,0.05))",
      },
      size: {
        xs: "h-7 rounded-md px-2.5 text-xs",
        sm: "h-8 rounded-md px-3 text-xs",
        default: "h-10 rounded-lg px-4 py-2",
        lg: "h-12 rounded-lg px-8 text-base",
        icon: "h-10 w-10 rounded-lg",
        "icon-sm": "h-7 w-7 rounded-md",
        // Opt-in (NO-791): classic sm; under the theme 36px tall (12px radius
        // is the theme's md radius).
        compact:
          "h-(--button-compact-height,2rem) rounded-md px-(--button-compact-px,0.75rem) text-xs",
      },
      shape: {
        default: "",
        pill: "rounded-full",
      },
    },
    // The press variant's pill. Each size keeps its own classic radius as the
    // fallback; the pill shape already rounds fully.
    compoundVariants: [
      {
        variant: "press",
        size: ["xs", "sm", "icon-sm", "compact"],
        shape: "default",
        className: "rounded-(--button-press-radius,var(--radius-md))",
      },
      {
        variant: "press",
        size: ["default", "lg", "icon"],
        shape: "default",
        className: "rounded-(--button-press-radius,var(--radius-lg))",
      },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
      shape: "default",
    },
  }
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, shape, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";

    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, shape }),
          FOCUS_RING_OFFSET,
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { buttonVariants };
